import { useQuery } from '@tanstack/react-query';
import { getWebsiteApiUrl } from 'config';
import { useTenant } from 'lib/hooks/useTenant';
import useTripQuery from './useTripQuery';

// What the API may return (the Lambda Faq fields are nullable).
export type RawTripFaq = {
  id?: number | null;
  question?: string | null;
  answer?: string | null;
  order?: number | null;
  isDefault?: boolean | null;
};

// A usable FAQ: finite id, non-empty question and answer.
export type TripFaq = {
  id: number;
  question: string;
  answer: string;
  order?: number | null;
  isDefault?: boolean | null;
};

type TripFaqsResponse = {
  data?: { pages?: { tripFaqs?: RawTripFaq[] | null } | null } | null;
  errors?: { message?: string }[];
};

export const TRIP_FAQS_QUERY = `
  query TripFaqs($tripId: Int!) {
    pages {
      tripFaqs(tripId: $tripId) {
        id
        question
        answer
        order
        isDefault
        category { id name type { title internal_name } }
        type { title internal_name }
      }
    }
  }
`;

// The API takes about 2-3 s on the deployed Lambda and 5-6 s from a developer
// machine. The FAQ section is below the fold and nothing else waits on it, so
// a generous timeout is cheap; it is also the worst case for the placeholder.
export const TRIP_FAQS_TIMEOUT_MS = 10_000;
const TRIP_FAQS_STALE_TIME_MS = 30 * 60 * 1000;

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const isUsableFaq = (faq: RawTripFaq | null): faq is TripFaq =>
  !!faq &&
  typeof faq.id === 'number' &&
  Number.isFinite(faq.id) &&
  hasText(faq.question) &&
  hasText(faq.answer);

export const getTripFaqsQueryKey = (tripId: number | undefined) =>
  ['trip-faqs', tripId] as const;

// Fetches the FAQs the website shows on this trip's product page. The API
// already returns the final list in the final order, so it is not re-sorted.
// An empty list means "the trip has no FAQs"; callers fall back to the general
// list for that and for every failure.
export const fetchTripFaqs = async (
  apiUrl: string,
  tripId: number,
  signal?: AbortSignal
): Promise<TripFaq[]> => {
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TRIP_FAQS_TIMEOUT_MS);
  const abortFromQuery = () => controller.abort();
  signal?.addEventListener('abort', abortFromQuery);

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: TRIP_FAQS_QUERY, variables: { tripId } }),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Trip FAQs request failed (${response.status})`);
    }

    const body = (await response.json()) as TripFaqsResponse;

    if (body.errors?.length) {
      throw new Error(
        `Trip FAQs query failed: ${body.errors[0]?.message ?? 'unknown error'}`
      );
    }

    const faqs = body.data?.pages?.tripFaqs;
    if (!Array.isArray(faqs)) {
      throw new Error('Trip FAQs response has an unexpected shape');
    }

    return faqs.filter(isUsableFaq);
  } catch (error) {
    // Only our own timeout is reported as one; an abort from the query
    // (unmount, cancel) keeps its original error.
    if (timedOut && !signal?.aborted) {
      throw new Error('Trip FAQs request timed out');
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abortFromQuery);
  }
};

// GWAT only. Reuses the trip query (same cache entry as the rest of the app)
// to get the inventory trip id. Stays disabled, and never touches the network,
// for DealsAway, before the trip id is known, and when the website API domain
// is not configured. `isLoading` is true only while a request that is going to
// happen is still unresolved, so callers can hold back the general list until
// the trip list has settled.
const useTripFaqsQuery = () => {
  const { isGwat } = useTenant();
  const { data: tripData, isLoading: isTripLoading } = useTripQuery();
  const apiUrl = getWebsiteApiUrl();
  const tripId = tripData?.result.trip.id;
  const isConfigured = isGwat && apiUrl !== null;

  const query = useQuery(
    getTripFaqsQueryKey(tripId),
    ({ signal }) => fetchTripFaqs(apiUrl as string, tripId as number, signal),
    {
      enabled: isConfigured && tripId !== undefined,
      staleTime: TRIP_FAQS_STALE_TIME_MS,
      // No retry: a failure falls back to the general list right away instead
      // of holding the placeholder through a second try (the worst case is
      // one TRIP_FAQS_TIMEOUT_MS).
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  return {
    faqs: query.data,
    isLoading: isConfigured && (isTripLoading || query.isInitialLoading),
  };
};

export { useTripFaqsQuery };
